import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, resetProductGridVisibility } from '@theme/utilities';
import { initCollectionProductCards } from './collection-product-card.js';

/** @type {Promise<typeof import('./split-tag-filters.js')> | null} */
let splitTagFiltersModulePromise = null;

/** @type {string | null} */
let pendingExcludeCountLoadingGroup = null;

/** @type {string | null} */
let pendingExcludeAvailabilityCountLoading = null;

/**
 * @returns {Promise<typeof import('./split-tag-filters.js')>}
 */
function loadSplitTagFiltersModule() {
  if (!splitTagFiltersModulePromise) {
    splitTagFiltersModulePromise = import('@theme/split-tag-filters');
  }

  return splitTagFiltersModulePromise;
}

function clearSplitTagFilterCache() {
  void loadSplitTagFiltersModule()
    .then(({ invalidateSplitTagFilterCache }) => invalidateSplitTagFilterCache())
    .catch(() => {});
}

/**
 * @param {ParentNode} scope
 * @param {string | null} [sectionId]
 */
function runCollectionFilterSideEffects(scope, sectionId = null) {
  void loadSplitTagFiltersModule()
    .then(({ applyCollectionFilterSideEffects }) => {
      void applyCollectionFilterSideEffects(scope, sectionId)
        .then(() => initCollectionProductCards())
        .catch(() => {});
    })
    .catch(() => {});
}

/** @type {() => void} */
let destroyFacetsToggleSticky = () => {
  document
    .querySelectorAll('.facets-toggle-sticky__sentinel, .facets-toggle-sticky__placeholder')
    .forEach((node) => node.remove());
};

/** @type {() => void} */
let initFacetsToggleSticky = () => {};

import('./facets-toggle-sticky.js')
  .then((module) => {
    if (typeof module.destroyFacetsToggleSticky === 'function') {
      destroyFacetsToggleSticky = module.destroyFacetsToggleSticky;
    }

    if (typeof module.initFacetsToggleSticky === 'function') {
      initFacetsToggleSticky = module.initFacetsToggleSticky;
    }
  })
  .catch(() => {});

/** @type {Map<string, number>} */
const sectionFilterUpdateGeneration = new Map();

/**
 * @param {string} sectionId
 * @returns {HTMLElement | null}
 */
function getSectionElement(sectionId) {
  return document.getElementById(`shopify-section-${sectionId}`);
}

/**
 * Syncs product grid markup from the Section Rendering API response.
 * Keeps the outer #ResultsList node stable so morph child indices stay aligned;
 * only replaces its inner wrapper (or inserts #ResultsList if morph dropped it).
 *
 * @param {HTMLElement | null | undefined} sectionElement
 * @param {string} sectionHTML
 */
/**
 * @returns {{ open: boolean, scrollTop: number, openDetailsIds: string[] }}
 */
function captureFiltersDrawerState() {
  const dialog = document.querySelector('#filters-drawer dialog.facets--drawer');

  if (!(dialog instanceof HTMLDialogElement) || !dialog.open) {
    return { open: false, scrollTop: 0, openDetailsIds: [] };
  }

  const formWrapper = dialog.querySelector('facets-form-component.facets__form-wrapper');

  return {
    open: true,
    scrollTop: formWrapper instanceof HTMLElement ? formWrapper.scrollTop : 0,
    openDetailsIds: [...dialog.querySelectorAll('details[open]')]
      .map((details) => details.id)
      .filter(Boolean),
  };
}

/**
 * @returns {DialogComponent | null}
 */
function getFiltersDrawerComponent() {
  const component = document.querySelector('#filters-drawer');
  return component instanceof DialogComponent ? component : null;
}

/**
 * Returns true when the collection grid has no visible products after filtering.
 *
 * @param {ParentNode} scope
 * @returns {boolean}
 */
function hasEmptyFilterResults(scope) {
  if (scope.querySelector('.main-collection-grid__empty:not([hidden])')) return true;

  const resultsList = scope.querySelector('#ResultsList');
  if (!resultsList) return false;

  const productGrid = scope.querySelector('#ResultsList .product-grid');
  if (!(productGrid instanceof HTMLElement)) return false;

  const visibleItems = scope.querySelectorAll(
    '#ResultsList .product-grid__item[data-variant-id]:not([data-collection-promo]):not([hidden]):not([data-split-tag-rejected])'
  );

  return visibleItems.length === 0;
}

/**
 * Restores accordion open state and scroll position inside the open drawer.
 *
 * @param {{ scrollTop: number, openDetailsIds: string[] }} state
 */
function applyFiltersDrawerUiState(state) {
  const dialog = document.querySelector('#filters-drawer dialog.facets--drawer');
  if (!(dialog instanceof HTMLDialogElement)) return;

  for (const id of state.openDetailsIds) {
    const details = document.getElementById(id);

    if (details instanceof HTMLDetailsElement) {
      details.open = true;
    }
  }

  const formWrapper = dialog.querySelector('facets-form-component.facets__form-wrapper');

  if (formWrapper instanceof HTMLElement && state.scrollTop > 0) {
    formWrapper.scrollTop = state.scrollTop;
  }
}

/**
 * Re-promotes a demoted open <dialog> back into the top layer without calling
 * `dialog.close()` (which triggers the slide-out close animation).
 *
 * @param {HTMLDialogElement} dialog
 * @param {DialogComponent | null} component
 */
function ensureFiltersDrawerModal(dialog, component) {
  if (!dialog.open) return;

  let isModal;
  try {
    isModal = dialog.matches(':modal');
  } catch {
    return;
  }

  if (isModal) return;

  dialog.removeAttribute('open');
  dialog.classList.remove('dialog-closing');
  dialog.setAttribute('data-filter-drawer-restoring', '');

  if (component) {
    component.showDialog();
  } else {
    dialog.showModal();
  }

  requestAnimationFrame(() => {
    dialog.removeAttribute('data-filter-drawer-restoring');
  });
}

/**
 * Ensures the filter drawer stays open after a filter update and restores its UI state.
 * The drawer must only close via the close button, backdrop click, or "Show Results".
 *
 * @param {{ open: boolean, scrollTop: number, openDetailsIds: string[] }} state
 */
function keepFiltersDrawerOpen(state) {
  if (!state.open) return;

  const component = getFiltersDrawerComponent();
  const dialog = document.querySelector('#filters-drawer dialog.facets--drawer');

  if (!(dialog instanceof HTMLDialogElement)) return;

  // Only (re)open when the drawer is fully closed. Never tear down an already-open
  // drawer (close + re-showModal) — that produces a visible close/reopen flash on every
  // filter apply and disrupts the in-flight filter interaction. The drawer must only
  // close via the close button, backdrop click, or "Show Results".
  if (!dialog.open) {
    if (component) {
      component.showDialog();
    } else {
      dialog.showModal();
    }
  }

  document.documentElement.setAttribute('scroll-lock', '');
  applyFiltersDrawerUiState(state);
  ensureFiltersDrawerModal(dialog, component);
}

/**
 * Keeps the drawer open and in the same state after a filter update.
 *
 * @param {{ open: boolean, scrollTop: number, openDetailsIds: string[] }} state
 * @param {ParentNode} scope
 */
function syncFiltersDrawerAfterUpdate(state, scope) {
  if (!state.open) return;

  keepFiltersDrawerOpen(state);
}

/**
 * Returns true when only sort (or page) changed between two collection URLs.
 *
 * @param {URL} previousUrl
 * @param {URL} newUrl
 * @returns {boolean}
 */
function isSortOnlyUpdate(previousUrl, newUrl) {
  /** @param {URL} url */
  const getComparableParams = (url) => {
    const params = new URLSearchParams();

    for (const [key, value] of url.searchParams.entries()) {
      if (key === 'sort_by' || key === 'page') continue;
      params.append(key, value);
    }

    return params.toString();
  };

  if (getComparableParams(previousUrl) !== getComparableParams(newUrl)) return false;

  return previousUrl.searchParams.get('sort_by') !== newUrl.searchParams.get('sort_by');
}

/**
 * Force-closes the drawer without waiting for close animation (used before DOM replace).
 */
function forceCloseFiltersDrawerForReplace() {
  const dialog = document.querySelector('#filters-drawer dialog.facets--drawer');

  if (dialog instanceof HTMLDialogElement) {
    dialog.classList.remove('dialog-closing');
    if (dialog.open) {
      dialog.close();
    }
  }

  document.documentElement.removeAttribute('scroll-lock');
}

/**
 * Updates drawer filter markup from section HTML.
 *
 * When the drawer is open, only the inner `.facets__drawer-layout` is swapped so the
 * `<dialog>` element itself stays open — no close/reopen flash. When closed, the whole
 * `#filters-drawer` node is replaced.
 *
 * @param {HTMLElement | null | undefined} sectionElement
 * @param {string} sectionHTML
 * @param {{ open: boolean, scrollTop: number, openDetailsIds: string[] }} [drawerState]
 */
function syncFiltersDrawerFromSectionHTML(sectionElement, sectionHTML, drawerState) {
  if (typeof sectionHTML !== 'string') return;

  const section =
    sectionElement instanceof HTMLElement && sectionElement.isConnected
      ? sectionElement
      : sectionElement?.id
        ? document.getElementById(sectionElement.id)
        : null;

  if (!(section instanceof HTMLElement)) return;

  const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
  const parsedSection = parsed.getElementById(section.id);
  if (!parsedSection) return;

  const newDrawer = parsedSection.querySelector('#filters-drawer');
  const liveDrawer = section.querySelector('#filters-drawer');

  if (!(newDrawer instanceof HTMLElement) || !(liveDrawer instanceof HTMLElement)) return;

  const liveDialog = liveDrawer.querySelector('dialog.facets--drawer');
  const drawerIsOpen = liveDialog instanceof HTMLDialogElement && liveDialog.open;

  // While the drawer is open, NEVER replace its live DOM. Re-creating the form and its
  // custom elements (facets-form-component, facet-inputs-component, accordion-custom, …)
  // can orphan declarative `on:change` bindings and break the entire drawer on real
  // devices where modules upgrade asynchronously. The user's checkbox state is already
  // correct, so we only refresh count numbers and availability in place.
  if (drawerIsOpen && drawerState?.open) {
    syncDrawerContentInPlace(liveDrawer, newDrawer);
    document.documentElement.setAttribute('scroll-lock', '');
    return;
  }

  // Drawer was closed — safe to replace the whole node.
  forceCloseFiltersDrawerForReplace();
  liveDrawer.replaceWith(document.importNode(newDrawer, true));
}

/**
 * Refreshes the open filter drawer from section HTML without replacing the drawer
 * shell (which would break custom-element bindings). Syncs counts, checkbox state,
 * remove pills, and the active-filter bubble from the server response.
 *
 * @param {HTMLElement} liveDrawer
 * @param {HTMLElement} newDrawer
 */
function syncDrawerContentInPlace(liveDrawer, newDrawer) {
  const useClientSideProductCount = document.querySelector(
    'facets-form-component[data-split-tag-filters="true"]'
  );

  if (!useClientSideProductCount) {
    const liveResultCounts = liveDrawer.querySelectorAll('.products-count-wrapper > span');
    const newResultCounts = newDrawer.querySelectorAll('.products-count-wrapper > span');
    liveResultCounts.forEach((span, index) => {
      const fresh = newResultCounts[index];
      if (fresh) span.textContent = fresh.textContent;
    });
  }

  /** @type {Map<string, HTMLInputElement>} */
  const newInputsById = new Map();
  newDrawer.querySelectorAll('input.checkbox__input[id]').forEach((input) => {
    if (input instanceof HTMLInputElement) newInputsById.set(input.id, input);
  });

  liveDrawer.querySelectorAll('input.checkbox__input[id]').forEach((liveInput) => {
    if (!(liveInput instanceof HTMLInputElement)) return;

    const newInput = newInputsById.get(liveInput.id);
    if (!newInput) return;

    liveInput.checked = newInput.checked;

    if (newInput.hasAttribute('disabled') && !liveInput.checked) {
      liveInput.setAttribute('disabled', '');
    } else {
      liveInput.removeAttribute('disabled');
    }

    // Split-tag (n) values and availability counts are recalculated client-side.
    if (liveInput.name === 'filter.p.tag' && useClientSideProductCount) {
      return;
    }

    const liveCountWrapper = liveInput.closest('.checkbox')?.querySelector('.checkbox__label-count');

    if (liveInput.name !== 'filter.v.availability') {
      const liveCount = liveInput.parentElement?.querySelector('.checkbox__label-count__value');
      const newCount = newInput.parentElement?.querySelector('.checkbox__label-count__value');

      if (liveCount instanceof HTMLElement && newCount instanceof HTMLElement) {
        liveCount.textContent = newCount.textContent;
      }

      if (liveCountWrapper instanceof HTMLElement) {
        liveCountWrapper.removeAttribute('data-count-loading');
      }
    }
  });

  // Price facet — sync text inputs, then sliders + track fill (range inputs have no name attr)
  liveDrawer.querySelectorAll('price-facet-component').forEach((liveFacet) => {
    if (!(liveFacet instanceof PriceFacetComponent)) return;

    const facetId = liveFacet.id;
    if (!facetId) return;

    const newFacet = newDrawer.querySelector(`price-facet-component#${CSS.escape(facetId)}`);
    if (!(newFacet instanceof HTMLElement)) return;

    liveFacet.querySelectorAll('input[name]').forEach((liveInput) => {
      if (!(liveInput instanceof HTMLInputElement)) return;

      const newInput = newFacet.querySelector(`input[name="${CSS.escape(liveInput.name)}"]`);
      if (!(newInput instanceof HTMLInputElement)) return;

      liveInput.value = newInput.value;
    });

    liveFacet.syncTrackFromInputs();
  });

  // Active-filter bubble in drawer title
  const liveBubble = liveDrawer.querySelector('.facets-drawer__title .facets__bubble');
  const newBubble = newDrawer.querySelector('.facets-drawer__title .facets__bubble');

  if (liveBubble instanceof HTMLElement && newBubble instanceof HTMLElement) {
    liveBubble.textContent = newBubble.textContent;
  } else if (liveBubble instanceof HTMLElement && !newBubble) {
    liveBubble.remove();
  } else if (!liveBubble && newBubble instanceof HTMLElement) {
    const liveTitle = liveDrawer.querySelector('.facets-drawer__title');
    if (liveTitle instanceof HTMLElement) {
      liveTitle.append(document.importNode(newBubble, true));
    }
  }

  // Remove pills — must refresh so data-url, labels, and facet-remove-component bindings
  // match the current filter state while the drawer stays open.
  const liveRemove = liveDrawer.querySelector('.facets-remove.facets-remove--mobile-and-vertical');
  const newRemove = newDrawer.querySelector('.facets-remove.facets-remove--mobile-and-vertical');

  if (liveRemove instanceof HTMLElement && newRemove instanceof HTMLElement) {
    liveRemove.replaceWith(document.importNode(newRemove, true));
  }
}

/**
 * Shows count shimmer on results totals and cross-group filter counts.
 * Skips the split-tag group that triggered the update so same-group (n) values
 * stay visible while the opposite group refetches.
 *
 * @param {ParentNode} scope
 * @param {{ resetPending?: boolean }} [options]
 */
function setSplitTagCountLoading(scope, options = {}) {
  const { resetPending = false } = options;

  if (!scope.querySelector('facets-form-component[data-split-tag-filters="true"]')) return;

  const excludeTagGroup =
    pendingExcludeCountLoadingGroup ??
    scope
      .querySelector('facets-form-component[data-split-tag-filters="true"]')
      ?.getAttribute('data-exclude-count-loading-group');

  const excludeAvailabilityValue =
    pendingExcludeAvailabilityCountLoading ??
    scope
      .querySelector('facets-form-component[data-split-tag-filters="true"]')
      ?.getAttribute('data-exclude-availability-count-loading');

  scope.querySelectorAll('.products-count-wrapper').forEach((el) => {
    el.setAttribute('data-count-loading', '');
  });

  scope
    .querySelectorAll(
      'facets-form-component[data-split-tag-filters="true"] facet-inputs-component[data-tag-filter-group] .checkbox__label-count'
    )
    .forEach((el) => {
      const group = el.closest('facet-inputs-component')?.getAttribute('data-tag-filter-group');

      if (excludeTagGroup && group === excludeTagGroup) {
        el.removeAttribute('data-count-loading');
        return;
      }

      el.setAttribute('data-count-loading', '');
    });

  scope.querySelectorAll('input[type="checkbox"][name="filter.v.availability"]').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;

    const countEl = input.closest('.checkbox')?.querySelector('.checkbox__label-count');
    if (!(countEl instanceof HTMLElement)) return;

    // Skip the whole availability facet when the shopper clicked within it — same
    // as excluding an entire split-tag group on same-group clicks.
    if (excludeAvailabilityValue) {
      countEl.removeAttribute('data-count-loading');
      return;
    }

    countEl.setAttribute('data-count-loading', '');
  });

  if (!resetPending) return;

  pendingExcludeCountLoadingGroup = null;
  pendingExcludeAvailabilityCountLoading = null;

  scope
    .querySelectorAll('facets-form-component[data-exclude-count-loading-group]')
    .forEach((form) => {
      form.removeAttribute('data-exclude-count-loading-group');
    });

  scope
    .querySelectorAll('facets-form-component[data-exclude-availability-count-loading]')
    .forEach((form) => {
      form.removeAttribute('data-exclude-availability-count-loading');
    });
}

/**
 * @param {ParentNode} scope
 */
function applySplitTagCountLoading(scope) {
  setSplitTagCountLoading(scope, { resetPending: true });
}

/**
 * Morph options for collection filter updates — skip nodes we replace from section HTML.
 *
 * @param {boolean} drawerWasOpen
 * @returns {import('@theme/morph').Options}
 */
function createFilterUpdateMorphOptions(drawerWasOpen) {
  return {
    beforeMorph() {
      // Must run immediately before morph — not before the network fetch. On slow mobile
      // connections any gap allows id-less sticky helper nodes to be re-inserted and
      // misalign `.collection-wrapper` children.
      destroyFacetsToggleSticky();
    },
    reject(oldNode) {
      if (!(oldNode instanceof HTMLElement)) return false;

      if (drawerWasOpen && oldNode.id === 'filters-drawer') {
        return true;
      }

      if (oldNode.id?.startsWith('collection-facets-toggle-')) {
        return true;
      }

      if (oldNode.id === 'ResultsList') {
        return true;
      }

      return false;
    },
  };
}

/**
 * Replaces or inserts the mobile facets toggle bar from section HTML.
 *
 * @param {HTMLElement | null | undefined} sectionElement
 * @param {string} sectionHTML
 */
function syncFacetsToggleFromSectionHTML(sectionElement, sectionHTML) {
  if (typeof sectionHTML !== 'string') return;

  const section =
    sectionElement instanceof HTMLElement && sectionElement.isConnected
      ? sectionElement
      : sectionElement?.id
        ? document.getElementById(sectionElement.id)
        : null;

  if (!(section instanceof HTMLElement)) return;

  const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
  const parsedSection = parsed.getElementById(section.id);
  if (!parsedSection) return;

  const parsedWrapper = parsedSection.querySelector('.collection-wrapper');
  const liveWrapper = section.querySelector('.collection-wrapper');
  if (!(parsedWrapper instanceof HTMLElement) || !(liveWrapper instanceof HTMLElement)) return;

  const newToggle = parsedWrapper.querySelector('.facets-toggle--horizontal-mobile');
  if (!(newToggle instanceof HTMLElement)) return;

  const importedToggle = document.importNode(newToggle, true);
  const liveToggle = liveWrapper.querySelector(
    '.facets-toggle--horizontal-mobile, [id^="collection-facets-toggle-"]'
  );

  if (liveToggle instanceof HTMLElement) {
    liveToggle.replaceWith(importedToggle);
    return;
  }

  const drawer = liveWrapper.querySelector('#filters-drawer');
  if (drawer instanceof HTMLElement) {
    drawer.before(importedToggle);
    return;
  }

  const facetsBar = liveWrapper.querySelector('.facets-block-wrapper--horizontal');
  if (facetsBar instanceof HTMLElement) {
    facetsBar.after(importedToggle);
    return;
  }

  liveWrapper.prepend(importedToggle);
}

/**
 * @param {HTMLElement | null | undefined} sectionElement
 * @param {string} sectionHTML
 * @returns {boolean} Whether #ResultsList is present after sync
 */
function syncProductGridFromSectionHTML(sectionElement, sectionHTML) {
  if (typeof sectionHTML !== 'string') return false;

  const section =
    sectionElement instanceof HTMLElement && sectionElement.isConnected
      ? sectionElement
      : sectionElement?.id
        ? document.getElementById(sectionElement.id)
        : null;

  if (!(section instanceof HTMLElement)) return false;

  const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
  const parsedSection = parsed.getElementById(section.id);
  if (!parsedSection) return false;

  const parsedWrapper = parsedSection.querySelector('.collection-wrapper');
  const liveWrapper = section.querySelector('.collection-wrapper');
  if (!(parsedWrapper instanceof HTMLElement) || !(liveWrapper instanceof HTMLElement)) return false;

  const newResultsList = parsedSection.querySelector('#ResultsList');
  if (!(newResultsList instanceof HTMLElement)) return false;

  const importedResultsList = document.importNode(newResultsList, true);
  const liveResultsList = liveWrapper.querySelector('#ResultsList');

  if (liveResultsList instanceof HTMLElement) {
    liveResultsList.replaceWith(importedResultsList);
  } else {
    const insertAnchor =
      liveWrapper.querySelector('[id^="collection-product-grid-script-"]') ??
      [...liveWrapper.querySelectorAll(':scope > script')].find((script) =>
        script.textContent?.includes('product-grid-view')
      ) ??
      liveWrapper.querySelector(':scope > style[data-shopify]');

    if (insertAnchor) {
      liveWrapper.insertBefore(importedResultsList, insertAnchor);
    } else {
      liveWrapper.appendChild(importedResultsList);
    }
  }

  return Boolean(section.querySelector('#ResultsList'));
}

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';
const SEARCH_TYPE = 'type';
const SEARCH_PRODUCT_TYPE = 'product';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    /** @type {URLSearchParams} */
    const newParameters = new URLSearchParams();
    /** @type {Set<string>} */
    const tagFilterValues = new Set();

    for (const [key, value] of formData.entries()) {
      if (typeof value !== 'string' || value === '') continue;

      if (key === 'filter.v.price.gte' || key === 'filter.v.price.lte') {
        newParameters.set(key, value);
        continue;
      }

      if (key === 'sort_by') {
        newParameters.set(key, value);
        continue;
      }

      if (key === 'filter.p.tag') {
        if (!tagFilterValues.has(value)) {
          tagFilterValues.add(value);
          newParameters.append(key, value);
        }
        continue;
      }

      if (key === 'filter.v.availability') {
        newParameters.set(key, value);
        continue;
      }

      newParameters.append(key, value);
    }

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) {
      newParameters.set(SEARCH_QUERY, searchQuery);
      if (this.#isSearchPage()) newParameters.set(SEARCH_TYPE, SEARCH_PRODUCT_TYPE);
    }

    return newParameters;
  }

  /**
   * @returns {boolean}
   */
  #isSearchPage() {
    const searchPath = new URL(Theme.routes.search_url, window.location.origin).pathname;
    return window.location.pathname === searchPath;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Builds the collection URL for the current filter state.
   *
   * For forms that only contain sort (no filter inputs), active filter params are
   * preserved from the current URL so that sorting doesn't clear selected filters.
   *
   * @returns {URL}
   */
  #buildFilterUrl() {
    const url = new URL(window.location.pathname, window.location.origin);
    const formParams = this.createURLParameters();

    // If this form has no filter inputs at all, it is a sort-only form.
    // Preserve active filter params from the current URL so they are not lost.
    const formHasFilterInputs = Boolean(
      this.refs.facetsForm?.querySelector('input[name^="filter."]')
    );

    if (!formHasFilterInputs) {
      const currentUrl = new URL(window.location.href);
      for (const [key, value] of currentUrl.searchParams) {
        if (key.startsWith('filter.')) {
          url.searchParams.append(key, value);
        }
      }
    }

    for (const [param, value] of formParams.entries()) {
      url.searchParams.append(param, value);
    }

    return url;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = this.#buildFilterUrl();
    const urlParameters = url.searchParams;

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   * @param {{ scrollToResults?: boolean }} [options]
   */
  updateFilters = (options = {}) => {
    const previousUrl = new URL(window.location.href);
    const filterUrl = this.#buildFilterUrl();
    const urlParameters = filterUrl.searchParams;
    const preserveScrollPosition =
      options.scrollToResults === false || isSortOnlyUpdate(previousUrl, filterUrl);

    history.pushState({ urlParameters: urlParameters.toString() }, '', filterUrl.toString());
    this.dispatchEvent(new FilterUpdateEvent(urlParameters));
    this.#updateSection(filterUrl, { preserveScrollPosition });
  };

  /**
   * Updates the section
   * @param {URL} filterUrl
   * @param {{ preserveScrollPosition?: boolean }} [options]
   */
  #updateSection(filterUrl = this.#buildFilterUrl(), options = {}) {
    const { preserveScrollPosition = false } = options;

    destroyFacetsToggleSticky();

    const preservedScrollY = preserveScrollPosition ? window.scrollY : null;

    const generation = (sectionFilterUpdateGeneration.get(this.sectionId) ?? 0) + 1;
    sectionFilterUpdateGeneration.set(this.sectionId, generation);

    const sectionElement = getSectionElement(this.sectionId);

    // Always render page 1 of the filtered collection. If infinite scroll
    // left ?page=2 in the address bar, including it would return an empty
    // product slice while facets still show the correct total count.
    const sectionRenderUrl = new URL(filterUrl.toString());
    sectionRenderUrl.searchParams.delete('page');

    sectionRenderer.invalidateCache(this.sectionId);
    clearSplitTagFilterCache();

    const filtersDrawerState = captureFiltersDrawerState();

    /** @param {string} sectionHTML */
    const afterSectionUpdate = (sectionHTML) => {
      const isCurrentGeneration = sectionFilterUpdateGeneration.get(this.sectionId) === generation;
      const liveSection = getSectionElement(this.sectionId) ?? sectionElement;
      const scope = liveSection ?? document;
      const gridMissing = !scope.querySelector('#ResultsList');

      // A stale response can still drop #ResultsList during morph — always restore it.
      if (!isCurrentGeneration && !gridMissing) return;

      if (!isCurrentGeneration && gridMissing) {
        syncFiltersDrawerFromSectionHTML(liveSection, sectionHTML, filtersDrawerState);
        applySplitTagCountLoading(scope);
        syncFacetsToggleFromSectionHTML(liveSection, sectionHTML);
        syncProductGridFromSectionHTML(liveSection, sectionHTML);
        resetProductGridVisibility(scope);
        runCollectionFilterSideEffects(scope, this.sectionId);
        syncFiltersDrawerAfterUpdate(filtersDrawerState, scope);
        initFacetsToggleSticky();
        return;
      }

      syncFiltersDrawerFromSectionHTML(liveSection, sectionHTML, filtersDrawerState);

      // Re-apply shimmer after drawer in-place sync (which must not touch split-tag
      // counts) and before grid sync so stale totals are never painted.
      applySplitTagCountLoading(scope);

      syncFacetsToggleFromSectionHTML(liveSection, sectionHTML);
      syncProductGridFromSectionHTML(liveSection, sectionHTML);
      resetProductGridVisibility(scope);
      runCollectionFilterSideEffects(scope, this.sectionId);
      syncFiltersDrawerAfterUpdate(filtersDrawerState, scope);
      initFacetsToggleSticky();
      initCollectionProductCards();

      const resultsList = scope.querySelector('results-list');
      if (resultsList instanceof Component) {
        resultsList.updatedCallback();
      }

      const drawerBlocksResults =
        filtersDrawerState.open && !hasEmptyFilterResults(scope);

      // Scroll to results unless the filter drawer is open — same behaviour as desktop.
      if (preservedScrollY !== null) {
        window.scrollTo({ top: preservedScrollY, behavior: 'instant' });
      } else if (!drawerBlocksResults && resultsList instanceof HTMLElement) {
        window.scrollTo({ top: resultsList.getBoundingClientRect().top + window.scrollY, behavior: 'instant' });
      }

      document.dispatchEvent(
        new CustomEvent(ThemeEvents.SectionRendered, {
          detail: { sectionId: this.sectionId },
        })
      );

      pendingExcludeCountLoadingGroup = null;
    };

    const renderOptions = {
      cache: false,
      url: sectionRenderUrl,
      source: 'facets-filter-update',
      morphOptions: createFilterUpdateMorphOptions(filtersDrawerState.open),
    };

    void sectionRenderer.renderSection(this.sectionId, renderOptions).then(afterSectionUpdate);
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    // Resolve against the current page URL, not origin. Clear-all on search uses a
    // query-only href (`?q=…`); with origin as base that becomes `/?q=…` and the
    // section render loses search context (`search.results` is not paginatable).
    const resolvedUrl = new URL(url, window.location.href);
    history.pushState('', '', resolvedUrl);
    this.dispatchEvent(new FilterUpdateEvent(resolvedUrl.searchParams));
    this.#updateSection(resolvedUrl);
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#bindChangeListener();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindChangeListener();
  }

  disconnectedCallback() {
    this.#unbindChangeListener();
    super.disconnectedCallback();
  }

  #bindChangeListener() {
    if (this.#changeListenerBound) return;

    this.addEventListener('change', this.#handleInputChange);
    this.#changeListenerBound = true;
  }

  #unbindChangeListener() {
    if (!this.#changeListenerBound) return;

    this.removeEventListener('change', this.#handleInputChange);
    this.#changeListenerBound = false;
  }

  /** @type {boolean} */
  #changeListenerBound = false;

  get sectionId() {
    const facetsForm = this.closest('facets-form-component');
    const sectionId = facetsForm?.getAttribute('section-id');

    if (sectionId) return sectionId;

    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');

    return id.replace(/^shopify-section-/, '');
  }

  #handleInputChange = (/** @type {Event} */ event) => {
    const { target } = event;

    if (!(target instanceof HTMLInputElement)) return;
    if (!target.name?.startsWith('filter.')) return;

    if (this.hasAttribute('data-tag-filter-group')) {
      this.#enforceSingleSelectInGroup(target);
    }

    if (target.name === 'filter.v.availability') {
      this.#enforceSingleSelectAvailability(target);
      pendingExcludeAvailabilityCountLoading = target.checked ? target.value : null;
      pendingExcludeCountLoadingGroup = null;

      const facetsForm = this.closest('facets-form-component');
      if (facetsForm instanceof FacetsFormComponent) {
        facetsForm.removeAttribute('data-exclude-count-loading-group');

        if (pendingExcludeAvailabilityCountLoading) {
          facetsForm.setAttribute(
            'data-exclude-availability-count-loading',
            pendingExcludeAvailabilityCountLoading
          );
        } else {
          facetsForm.removeAttribute('data-exclude-availability-count-loading');
        }
      }
    }

    this.updateFilters({ scrollToResults: !this.closest('#filters-drawer') });
  };

  /**
   * In stock and out of stock are mutually exclusive (radio-style checkboxes).
   *
   * @param {HTMLInputElement} selectedInput
   */
  #enforceSingleSelectAvailability(selectedInput) {
    if (!selectedInput.checked) return;

    this.querySelectorAll('input[type="checkbox"][name="filter.v.availability"]').forEach((input) => {
      if (input instanceof HTMLInputElement && input !== selectedInput) {
        input.checked = false;
      }
    });
  }

  /**
   * Keeps at most one checked value per split tag filter group.
   *
   * @param {HTMLInputElement} selectedInput
   */
  #enforceSingleSelectInGroup(selectedInput) {
    if (!selectedInput.checked) return;

    this.querySelectorAll(`input[type="checkbox"][name="${selectedInput.name}"]`).forEach((input) => {
      if (input instanceof HTMLInputElement && input !== selectedInput) {
        input.checked = false;
      }
    });
  }

  /**
   * Updates filters and the selected facet summary
   * @param {{ scrollToResults?: boolean }} [options]
   */
  updateFilters(options = {}) {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const tagGroup = this.getAttribute('data-tag-filter-group');
    if (tagGroup) {
      pendingExcludeCountLoadingGroup = tagGroup;
      facetsForm.setAttribute('data-exclude-count-loading-group', tagGroup);
      pendingExcludeAvailabilityCountLoading = null;
      facetsForm.removeAttribute('data-exclude-availability-count-loading');
    } else {
      pendingExcludeCountLoadingGroup = null;
      facetsForm.removeAttribute('data-exclude-count-loading-group');
    }

    if (tagGroup || pendingExcludeAvailabilityCountLoading) {
      const section = facetsForm.closest('.shopify-section');
      if (section instanceof HTMLElement) {
        setSplitTagCountLoading(section, { resetPending: false });
      }
    }

    facetsForm.updateFilters(options);
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;

      if (this.hasAttribute('data-tag-filter-group')) {
        this.#enforceSingleSelectInGroup(closestInput);
      }

      if (closestInput.name === 'filter.v.availability') {
        this.#enforceSingleSelectAvailability(closestInput);
        pendingExcludeAvailabilityCountLoading = closestInput.checked ? closestInput.value : null;
        pendingExcludeCountLoadingGroup = null;

        const facetsForm = this.closest('facets-form-component');
        if (facetsForm instanceof FacetsFormComponent) {
          facetsForm.removeAttribute('data-exclude-count-loading-group');

          if (pendingExcludeAvailabilityCountLoading) {
            facetsForm.setAttribute(
              'data-exclude-availability-count-loading',
              pendingExcludeAvailabilityCountLoading
            );
          } else {
            facetsForm.removeAttribute('data-exclude-availability-count-loading');
          }
        }
      }

      this.updateFilters({ scrollToResults: !this.closest('#filters-drawer') });
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url, 'facets-hover-prefetch');
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (this.hasAttribute('data-tag-filter-group')) return;
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 * @property {HTMLInputElement} minSlider - The minimum range slider
 * @property {HTMLInputElement} maxSlider - The maximum range slider
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('input', this.#onSliderInput);
    this.#syncSlidersFromInputs();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncSlidersFromInputs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('input', this.#onSliderInput);
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Syncs slider positions and track fill from current text input values.
   * Used after external DOM updates (drawer in-place sync, clear filter).
   */
  syncTrackFromInputs() {
    this.#syncSlidersFromInputs();
  }

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters({ scrollToResults: !this.closest('#filters-drawer') });
    this.#setMinAndMaxValues();
    this.#updateSummary();
    this.#syncSlidersFromInputs();
  }

  /**
   * Handles range slider `input` events — updates track + mirrors to text inputs.
   * The `change` event from the slider bubbles up to the component's `on:change`
   * delegation and triggers `updatePriceFilterAndResults` automatically.
   * @param {Event} event
   */
  #onSliderInput = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'range') return;

    const { minSlider, maxSlider, minInput, maxInput } = this.refs;
    if (!minSlider || !maxSlider) return;

    const minVal = parseFloat(minSlider.value);
    const maxVal = parseFloat(maxSlider.value);

    if (minVal > maxVal) {
      if (event.target === minSlider) minSlider.value = String(maxVal);
      else maxSlider.value = String(minVal);
    }

    minInput.value = minSlider.value !== minSlider.min ? minSlider.value : '';
    maxInput.value = maxSlider.value !== maxSlider.max ? maxSlider.value : '';

    this.#updateTrack();
  };

  /**
   * Syncs slider positions from current text input values.
   */
  #syncSlidersFromInputs() {
    const { minSlider, maxSlider, minInput, maxInput } = this.refs;
    if (!minSlider || !maxSlider) return;

    minSlider.value = minInput?.value || minSlider.min;
    maxSlider.value = maxInput?.value || maxSlider.max;

    this.#updateTrack();
  }

  /**
   * Updates the CSS custom properties that drive the track fill gradient.
   */
  #updateTrack() {
    const { minSlider, maxSlider } = this.refs;
    if (!minSlider || !maxSlider) return;

    const min = parseFloat(minSlider.min) || 0;
    const max = parseFloat(minSlider.max) || 1;
    const range = max - min;

    if (range <= 0) return;

    const leftPct = Math.max(0, Math.min(100, ((parseFloat(minSlider.value) - min) / range) * 100));
    const rightPct = Math.max(0, Math.min(100, ((parseFloat(maxSlider.value) - min) / range) * 100));

    this.style.setProperty('--price-range-left', `${leftPct}%`);
    this.style.setProperty('--price-range-right', `${rightPct}%`);
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');

    if (container instanceof FacetInputsComponent) {
      container.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
        if (input instanceof HTMLInputElement) input.checked = false;
      });
    } else if (container instanceof PriceFacetComponent) {
      const { minInput, maxInput } = container.refs;

      if (minInput instanceof HTMLInputElement) minInput.value = '';
      if (maxInput instanceof HTMLInputElement) maxInput.value = '';

      container.syncTrackFromInputs();
    }

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters({ scrollToResults: !this.closest('#filters-drawer') });
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The sorting details element
 *
 * @extends {Component<SortingFilterRefs>}
 */
class SortingFilterComponent extends Component {
  /** @type {AbortController | null} */
  #closeAnimationController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setupHorizontalCloseAnimation();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setupHorizontalCloseAnimation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#closeAnimationController?.abort();
    this.#closeAnimationController = null;
  }

  #setupHorizontalCloseAnimation() {
    if (!this.classList.contains('sorting-filter__horizontal')) return;

    this.#closeAnimationController?.abort();
    this.#closeAnimationController = new AbortController();
    const { signal } = this.#closeAnimationController;

    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;

    const summary = details.querySelector('summary');
    summary?.addEventListener('click', this.#handleSummaryClick, { capture: true, signal });

    const accordion = this.querySelector('accordion-custom');
    accordion?.addEventListener('keydown', this.#handleKeyDown, { capture: true, signal });

    document.addEventListener('click', this.#handleDocumentClick, { capture: true, signal });
  }

  /**
   * @returns {boolean}
   */
  #shouldAnimateClose() {
    return (
      this.classList.contains('sorting-filter__horizontal') &&
      window.innerWidth >= 750 &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getSortOptionsPanel() {
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return null;

    const panel = details.querySelector('floating-panel-component.sorting-filter__options');
    return panel instanceof HTMLElement ? panel : null;
  }

  #closeDetailsAnimated() {
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    const panel = this.#getSortOptionsPanel();
    if (!panel) {
      details.open = false;
      return;
    }

    if (panel.classList.contains('sorting-filter__options--closing')) return;

    panel.classList.add('sorting-filter__options--closing');

    let closed = false;

    const finishClose = () => {
      if (closed) return;
      closed = true;
      panel.classList.remove('sorting-filter__options--closing');
      details.open = false;
    };

    /** @param {TransitionEvent} transitionEvent */
    const onTransitionEnd = (transitionEvent) => {
      if (transitionEvent.target !== panel || transitionEvent.propertyName !== 'opacity') return;

      panel.removeEventListener('transitionend', onTransitionEnd);
      finishClose();
    };

    panel.addEventListener('transitionend', onTransitionEnd);

    const durationMs = Number.parseFloat(getComputedStyle(panel).transitionDuration) * 1000 || 325;

    window.setTimeout(() => {
      panel.removeEventListener('transitionend', onTransitionEnd);
      finishClose();
    }, durationMs + 50);
  }

  /** @param {MouseEvent} event */
  #handleSummaryClick = (event) => {
    if (!this.#shouldAnimateClose()) return;

    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    event.preventDefault();
    this.#closeDetailsAnimated();
  };

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    if (!this.#shouldAnimateClose()) return;

    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    if (event.target instanceof Node && details.contains(event.target)) return;

    event.stopPropagation();
    this.#closeDetailsAnimated();
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#shouldAnimateClose()) return;

    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#closeDetailsAnimated();
    details.querySelector('summary')?.focus();
  };

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters({ scrollToResults: false });
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    const { target } = event;

    if (!(target instanceof HTMLSelectElement) && !(target instanceof HTMLInputElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    /** @type {string} */
    let optionName = '';

    if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      optionName = selectedOption?.dataset.optionName ?? selectedOption?.textContent?.trim() ?? '';
    } else if (target.checked) {
      optionName = target.dataset.optionName ?? '';
    }

    const selectedSortValue = details.querySelector('[ref="selectedSortValue"]');
    if (selectedSortValue instanceof HTMLElement) {
      selectedSortValue.textContent = optionName;
    }

    const facetStatus = details.querySelector('.facets__status');
    if (facetStatus instanceof HTMLElement) {
      facetStatus.textContent = optionName;
    }
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
  }

  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
